/**
 * UnifiedBottomPanel — Shared floating bottom panel shell with tab system.
 *
 * Used by CharacterDesigner, LocationDesigner, ObjectDesigner, ImageGenerator,
 * and StoryboardDesigner.
 * This component provides only the panel container, tab bar, and open/close animation.
 * Tab content is rendered via a children render function (slot pattern).
 *
 * Follows floating panel pattern: nopan nodrag nowheel, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo, useEffect, useId, useRef, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SlidersHorizontal, Wand2, LayoutGrid, Mic, ShieldCheck, type LucideIcon } from 'lucide-react'
import { useNodeId, useReactFlow, useStore } from 'reactflow'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { useRegisterCanvasBottomPanelVisibility } from '../../CanvasBottomPanelVisibilityContext'
import { getTranslationToRevealRect } from './bottomPanelViewport'
import { useViewportZoom } from './viewportZoom'
import { useNodePresentation } from '../../NodePresentationContext'
import { useNuxTooltipEvents } from '../../../../onboarding/nuxEvents'

// The opening animation starts 10px above its resting position. A 32px
// measurement inset therefore still leaves a comfortable visible margin once
// the panel settles, without requiring a distracting second correction pan.
const VIEWPORT_PADDING_PX = 32
const VIEWPORT_PAN_DURATION_MS = 250
const viewportLog = createNadDebug('dw:canvas:bottom-panel-viewport')

// ---------------------------------------------------------------------------
// Tab configuration types & constants
// ---------------------------------------------------------------------------

export interface TabDefinition {
  id: string
  label: string
  icon: LucideIcon
  disabled?: boolean
  disabledTooltip?: string
}

/** CharacterDesigner: Config, Refine, Variants, Voice */
export const CHARACTER_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
  { id: 'voice', label: 'Voice', icon: Mic },
]

/** LocationDesigner: Config, Refine, Variants */
export const LOCATION_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ObjectDesigner: Config, Refine, Variants */
export const OBJECT_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ImageGenerator: Config, Refine, Variants */
export const IMAGE_GEN_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** StoryboardDesigner: Config, Refine, Variants */
export const STORYBOARD_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** Quality tab — conditionally added by node components when VLM validation is enabled */
export const QUALITY_TAB: TabDefinition = { id: 'quality', label: 'Quality', icon: ShieldCheck }

/** Default tab when panel first opens */
export const DEFAULT_TAB = 'config'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface UnifiedBottomPanelProps {
  /** Tab definitions for this node type */
  tabs: TabDefinition[]
  /** Currently active tab ID */
  activeTab: string
  /** Tab switch callback */
  onTabChange: (tabId: string) => void
  /** Whether the panel is visible/open */
  isVisible: boolean
  /** Optional fixed height for the content area in px, or auto height for compact tabs (default: 340px) */
  contentHeight?: number | 'auto'
  /** Optional minimum width (px) for non-variants tabs */
  minWidth?: number
  /**
   * Hide the tab bar entirely. Used by the dedicated Variants panel mode
   * (post 1533007285123236): when `activeTab === 'variants'`, Variants render
   * as a clean, tab-less box that zooms with the canvas, while Config/Refine/
   * Quality keep the tab bar and inverse-zoom. The existing
   * `activeTab === 'variants'` zoom split (below) already delivers canvas-zoom.
   */
  hideTabBar?: boolean
  /** Render function receiving the active tab ID — slot pattern for tab content */
  children: (activeTab: string) => ReactNode
  /** Optional footer rendered below the scroll area but inside the panel.
   *  Flyout popovers in the footer escape the overflow-hidden clipping. */
  footer?: (activeTab: string) => ReactNode
}

const UnifiedBottomPanel = memo(function UnifiedBottomPanel({
  isVisible,
  ...props
}: UnifiedBottomPanelProps) {
  const readOnly = useReadOnly()
  const inline = useNodePresentation() === 'detail'
  const nodeId = useNodeId()
  const fallbackPanelId = useId()

  useRegisterCanvasBottomPanelVisibility(nodeId ?? fallbackPanelId, isVisible && !inline)

  return (
    <AnimatePresence>
      {isVisible && (
        <VisibleUnifiedBottomPanel key="unified-bottom-panel" {...props} isVisible={isVisible} readOnly={readOnly} />
      )}
    </AnimatePresence>
  )
})

type VisibleUnifiedBottomPanelProps = UnifiedBottomPanelProps & {
  readOnly: boolean
}

const VisibleUnifiedBottomPanel = memo(function VisibleUnifiedBottomPanel({
  tabs: suppliedTabs,
  activeTab,
  onTabChange,
  contentHeight,
  minWidth,
  hideTabBar: suppliedHideTabBar = false,
  children,
  footer: _footer,
  readOnly,
}: VisibleUnifiedBottomPanelProps) {
  const { t } = useTranslation('canvas')
  const inline = useNodePresentation() === 'detail'
  // Canvas keeps its separate Variants button. The detail pane exposes that
  // same content alongside Config, without introducing another editor.
  const tabs = inline && !suppliedTabs.some((tab) => tab.id === 'variants')
    ? [...suppliedTabs, { id: 'variants', label: 'Variants', icon: LayoutGrid }]
    : suppliedTabs
  const hideTabBar = !inline && suppliedHideTabBar
  const tabPanelId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const nodeId = useNodeId()
  const nodeType = useStore((s) => (nodeId ? s.nodeInternals.get(nodeId)?.type : undefined))
  const { emitNuxEvent } = useNuxTooltipEvents()
  const { getViewport, setViewport } = useReactFlow()
  const activeTabDefinition = tabs.find((tab) => tab.id === activeTab)
  const zoom = useViewportZoom()
  const scale = activeTab === 'variants' ? 1 : Math.min(2, Math.max(0.5, 1 / zoom))

  useEffect(() => {
    emitNuxEvent({ type: 'panelOpened', panelId: activeTab, nodeId: nodeId ?? undefined, nodeType })
  }, [activeTab, emitNuxEvent, nodeId, nodeType])

  useEffect(() => {
    if (inline) return
    let firstFrame = 0
    let measurementFrame = 0

    // Measure after the panel has mounted and Framer Motion has applied its
    // opening transform. The extra frame keeps the pan visually paired with
    // the panel entrance while avoiding a zero-sized first measurement.
    firstFrame = window.requestAnimationFrame(() => {
      measurementFrame = window.requestAnimationFrame(() => {
        const panel = panelRef.current
        const canvas = panel?.closest<HTMLElement>('.react-flow')
        if (!panel || !canvas) return

        const panelRect = panel.getBoundingClientRect()
        const canvasRect = canvas.getBoundingClientRect()
        const translation = getTranslationToRevealRect(
          panelRect,
          canvasRect,
          VIEWPORT_PADDING_PX,
        )
        const viewport = getViewport()

        viewportLog({
          event: 'bottom-panel-open-visibility-check',
          feature: 'layout',
          node_id: nodeId ?? 'unknown',
          zoom: viewport.zoom,
          pan_x: Math.round(translation.x),
          pan_y: Math.round(translation.y),
          panel_bottom: Math.round(panelRect.bottom),
          canvas_bottom: Math.round(canvasRect.bottom),
        })

        if (translation.x === 0 && translation.y === 0) return

        void setViewport(
          {
            x: viewport.x + translation.x,
            y: viewport.y + translation.y,
            zoom: viewport.zoom,
          },
          { duration: VIEWPORT_PAN_DURATION_MS },
        )
      })
    })

    return () => {
      window.cancelAnimationFrame(firstFrame)
      window.cancelAnimationFrame(measurementFrame)
    }
  }, [getViewport, nodeId, setViewport, inline])

  const widthExpr = minWidth
    ? `max(calc(100% + 120px), ${minWidth}px)`
    : 'calc(100% + 120px)'
  const contentHeightStyle = contentHeight === 'auto'
    ? 'auto'
    : contentHeight
      ? `${contentHeight}px`
      : '340px'

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, y: -10, scale, x: inline ? 0 : '-50%' }}
      animate={{ opacity: 1, y: 0, scale, x: inline ? 0 : '-50%' }}
      exit={{ opacity: 0, y: -10, scale, x: inline ? 0 : '-50%' }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
        opacity: { duration: 0.15 },
        scale: { duration: 0 },
      }}
      className={cn('z-50 nopan nodrag nowheel', inline ? 'relative mt-4 w-full' : 'absolute')}
      style={inline ? undefined : {
        top: '100%',
        left: '50%',
        marginTop: 8,
        transformOrigin: 'top center',
        width: widthExpr,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-testid="unified-bottom-panel"
    >
      <div className="bg-surface-1 text-foreground border border-border rounded-xl shadow-2xl">
        {/* Tab Bar — hidden in the dedicated Variants panel mode */}
        {!hideTabBar && (
            <div
              className="flex gap-5 px-4 border-b border-border-subtle"
              role="tablist"
              aria-label={t('unifiedBottomPanel.tabListLabel')}
            >
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    id={`${tabPanelId}-tab-${tab.id}`}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={tabPanelId}
                    onClick={tab.disabled ? undefined : () => onTabChange(tab.id)}
                    disabled={tab.disabled}
                    // Label-only text tabs: the active tab is marked by gold
                    // label text and a gold underline sitting on the bar's
                    // bottom border, not by a solid `--primary` fill. Once the
                    // panel is open the user's focus is the prompt below, so the
                    // tab bar should not pull the eye (post 1640579001032730;
                    // "reverse colour + underline", as in Workplace's tabs).
                    className={cn(
                      'h-9 flex items-center px-1 text-xs font-medium uppercase tracking-wider relative transition-colors duration-200 group',
                      tab.disabled
                        ? 'opacity-40 cursor-not-allowed text-muted-foreground'
                        : isActive
                          ? 'text-primary'
                          : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`tab-${tab.id}`}
                    data-nux-anchor={tab.id === 'config' || tab.id === 'refine' ? 'node-config-refine-tabs' : undefined}
                    data-nux-panel-id={tab.id}
                    data-nux-node-id={nodeId ?? undefined}
                    data-nux-node-type={nodeType}
                  >
                    {t(`unifiedBottomPanel.${tab.id}`, tab.label)}
                    {isActive && (
                      <span
                        aria-hidden="true"
                        data-testid={`tab-underline-${tab.id}`}
                        className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-primary"
                      />
                    )}
                    {tab.disabled && tab.disabledTooltip && (
                      <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-surface-2 text-foreground text-xxs px-2 py-1 rounded shadow-lg border border-border opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {tab.disabledTooltip}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
        )}

        {/* Tab Content — overflow hidden, individual tabs handle scrolling */}
        <div
          id={tabPanelId}
          role={hideTabBar ? 'region' : 'tabpanel'}
          aria-label={
            hideTabBar
              ? t(
                  `unifiedBottomPanel.${activeTab}`,
                  activeTabDefinition?.label ?? activeTab,
                )
              : undefined
          }
          aria-labelledby={
            hideTabBar ? undefined : `${tabPanelId}-tab-${activeTab}`
          }
          className={cn(
            'rounded-b-xl nowheel overflow-hidden flex flex-col',
            readOnly && 'pointer-events-none select-text opacity-70',
          )}
          style={{ height: contentHeightStyle }}
          data-testid="tab-content-area"
        >
          {children(activeTab)}
        </div>
      </div>
    </motion.div>
  )
})

VisibleUnifiedBottomPanel.displayName = 'VisibleUnifiedBottomPanel'

UnifiedBottomPanel.displayName = 'UnifiedBottomPanel'

export { UnifiedBottomPanel }
